import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Truck, Car, DollarSign, Users, Calendar, TrendingUp,
  Plus, X, FileText, ClipboardList, Calculator, Navigation,
  CheckCircle, User, Clock, BarChart2, Receipt, Handshake, PieChart, Save, Zap
} from 'lucide-react';
import { toast } from 'sonner';

// Extracted tab components
import { MarketingCRMTab } from '../components/command-center/CCMarketingCRMTab'; // kept for any legacy refs (not mounted)
import QuickQuoteTab from '../components/command-center/QuickQuoteTab';
import RequestsInboxTab from '../components/command-center/RequestsInboxTab';
import OpsLogsTab from '../components/command-center/OpsLogsTab';
import ClientCRM from '../components/command-center/ClientCRM';
import DailyOpsTab from '../components/command-center/DailyOpsTab';
import WeeklyReviewTab from '../components/command-center/WeeklyReviewTab';
import MonthlyPLTab from '../components/command-center/MonthlyPLTab';
import CatalogEditorTab from '../components/command-center/CatalogEditorTab';
import BookingsVIPTab from '../components/command-center/BookingsVIPTab';
import RevenueCalendarTab from '../components/command-center/RevenueCalendarTab';
import DriverRosterTab from '../components/command-center/DriverRosterTab';
import ROITrackerTab from '../components/command-center/ROITrackerTab';
import FollowUpTab from '../components/command-center/FollowUpTab';
import QuoteContractBuilder from '../components/command-center/QuoteContractBuilder';
import InvoicesGeneratorTab from '../components/command-center/InvoicesGeneratorTab';
import AffiliateNetworkTab from '../components/command-center/AffiliateNetworkTab';
import PartnerNetworkTab from '../components/command-center/PartnerNetworkTab';
import FulfillmentControlTab from '../components/command-center/FulfillmentControlTab';
import VehiclePLTab from '../components/command-center/VehiclePLTab';
import CommissionsTab from '../components/command-center/CommissionsTab';
import QuoTab from '../components/command-center/QuoTab';
import RentalRequestsTab from '../components/command-center/RentalRequestsTab';
import OpsLogV3Tab from '../components/command-center/OpsLogV3Tab';
import CommandDashboardTab from '../components/command-center/CommandDashboardTab';
import OpsDashboardTab from '../components/command-center/OpsDashboardTab';
import OwnerPayoutsTab from '../components/command-center/OwnerPayoutsTab';
import DriverPayrollTab from '../components/command-center/DriverPayrollTab';
import MaintenanceTab from '../components/command-center/MaintenanceTab';
import ConditionReportsTab from '../components/command-center/ConditionReportsTab';
import GoogleCalendarButton from '../components/command-center/GoogleCalendarButton';
import AdminShell, { buildQuickActions } from '../components/layout/AdminShell';

// Pricing HQ sub-components
import BookingsTab from '../components/pricing/BookingsTab';
import VehiclesTab from '../components/pricing/VehiclesTab';
import AffiliatesTab from '../components/pricing/AffiliatesTab';
import DashboardTab from '../components/pricing/DashboardTab';
import QuoteTab from '../components/pricing/QuoteTab';
import JobsTab from '../components/pricing/JobsTab';
import { BookingModal, VehicleModal, AffiliateModal, CalculatorModal } from '../components/pricing/PricingModals';
import { getCurrentMonth } from '../components/pricing/PricingShared';
import ProfitCalculator from './ProfitCalculator';

const API = process.env.REACT_APP_BACKEND_URL;

const CommandCenter = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'ops';
  const [activeTab, setActiveTab] = useState(initialTab);
  // Keep tab in sync when other components navigate with ?tab=… (e.g. open client full page)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== activeTab) setActiveTab(t);
  }, [searchParams]); // eslint-disable-line
  // N7 retirement: the legacy quote/booking screen is retired — route staff to the native quoting workspace.
  useEffect(() => {
    if (searchParams.get('tab') === 'quotebookings') navigate('/os/services?tab=quotes', { replace: true });
  }, [searchParams]); // eslint-disable-line
  const [snapshotRunning, setSnapshotRunning] = useState(false);
  const [qqPrefillDate, setQqPrefillDate] = useState('');

  const handleCalendarQuickQuote = (dateKey) => {
    setQqPrefillDate(dateKey);
    handleTabChange('quotebookings');
    setTimeout(() => document.getElementById('quick-quote-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  };

  const handleSnapshotSeed = async () => {
    if (!window.confirm('Export the current database (fleet, photos, rentals, pricing) to seed files so the next deploy preserves them?')) return;
    setSnapshotRunning(true);
    try {
      const r = await fetch(`${API}/api/admin/snapshot-seed`, { method: 'POST' });
      if (!r.ok) throw new Error('Snapshot failed');
      const data = await r.json();
      toast.success(`Deploy snapshot saved — ${data.total_records} records across ${Object.keys(data.collections).length} collections`);
    } catch (e) {
      toast.error(e.message || 'Snapshot failed');
    }
    setSnapshotRunning(false);
  };

  // Swipe navigation for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const swipeThreshold = 50;
  const tabOrder = ['ops', 'dailyops', 'quotebookings', 'teampay', 'weeklypl', 'monthlypl', 'contracts', 'quo', 'clients'];

  const onTouchStart = (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const currentIndex = tabOrder.indexOf(activeTab);
    if (Math.abs(distance) > swipeThreshold) {
      if (distance > 0 && currentIndex < tabOrder.length - 1) handleTabChange(tabOrder[currentIndex + 1]);
      else if (distance < 0 && currentIndex > 0) handleTabChange(tabOrder[currentIndex - 1]);
    }
  };

  // Pricing HQ data
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [bookingSummary, setBookingSummary] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [packages, setPackages] = useState([]);
  const [bookingView, setBookingView] = useState('all');
  const [affiliateSort, setAffiliateSort] = useState('revenue');
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });
  const [vehicleSortConfig, setVehicleSortConfig] = useState({ field: 'vehicle_name', direction: 'asc' });
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [showAffiliateForm, setShowAffiliateForm] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickForm, setQuickForm] = useState({ client_name: '', client_phone: '', service_type: 'hourly', vehicle_name: '', date: new Date().toISOString().split('T')[0], pickup_time: '', hours: 3, pickup: '', dropoff: '', notes: '', retail_hourly: 0 });
  const [calcResult, setCalcResult] = useState(null);
  const [calcForm, setCalcForm] = useState({ hours: 4, service_type: 'concierge', pricing_type: 'retail', ownership: 'owned', retail_hourly: 150, affiliate_hourly: 120, rental_cost: 0, driver_rate: 30 });
  const [newBooking, setNewBooking] = useState({ date: new Date().toISOString().split('T')[0], service_type: 'concierge', vehicle_name: '', hours: 4, pricing_type: 'retail', ownership: 'owned', retail_hourly: 150, affiliate_hourly: 120, rental_cost: 0, driver_rate: 30, affiliate_id: '', notes: '' });
  const [newVehicle, setNewVehicle] = useState({ vehicle_name: '', category: 'concierge', ownership_default: 'owned', retail_hourly: 150, affiliate_hourly: 120, rental_low: 0, rental_high: 0, cost_basis: 0 });
  const [newAffiliate, setNewAffiliate] = useState({ affiliate_name: '', contact_email: '', contact_phone: '', commission_rate: 0 });
  const [quoteVehicle, setQuoteVehicle] = useState('first_class_suv');
  const [quoteHours, setQuoteHours] = useState(4);
  const [quotePricing, setQuotePricing] = useState('retail');
  const [quoteDriverRate, setQuoteDriverRate] = useState(30);
  const [quoteRentalCost, setQuoteRentalCost] = useState(0);
  const [quoteIsRental, setQuoteIsRental] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [rateCard, setRateCard] = useState({});
  const [liveJobs, setLiveJobs] = useState({ jobs: [], kpis: {} });

  // Auth check
  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (!r.ok) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  // Data loaders
  const loadBookings = async () => { try { const vp = bookingView !== 'all' ? `?view=${bookingView}` : ''; const r = await fetch(`${API}/api/pricing/bookings${vp}`); const d = await r.json(); setBookings(d.bookings || []); setBookingSummary(d.summary || {}); } catch {} };
  const loadVehicles = async () => { try { const r = await fetch(`${API}/api/pricing/vehicles`); setVehicles((await r.json()).vehicles || []); } catch {} };
  const loadAffiliates = async () => { try { const r = await fetch(`${API}/api/pricing/affiliates?sort_by=${affiliateSort}`); setAffiliates((await r.json()).affiliates || []); } catch {} };
  const loadMonthlyData = async () => { try { const r = await fetch(`${API}/api/pricing/dashboard/monthly`); setMonthlyData((await r.json()).months || []); } catch {} };
  const loadHeatmap = async () => { try { const r = await fetch(`${API}/api/pricing/dashboard/heatmap`); setHeatmapData((await r.json()).heatmap || []); } catch {} };
  const loadPackages = async () => { try { const r = await fetch(`${API}/api/pricing/packages/transportation`); setPackages((await r.json()).packages || []); } catch {} };
  const loadRateCard = async () => { try { const r = await fetch(`${API}/api/pricing/quote/rate-card`); setRateCard(await r.json()); } catch {} };
  const loadLiveJobs = async () => { try { const r = await fetch(`${API}/api/pricing/jobs/live`); setLiveJobs(await r.json()); } catch {} };

  const loadAllPricing = useCallback(async () => { setLoading(true); await Promise.all([loadBookings(), loadVehicles(), loadAffiliates(), loadMonthlyData(), loadHeatmap(), loadPackages(), loadRateCard(), loadLiveJobs()]); setLoading(false); }, []);
  useEffect(() => { loadAllPricing(); }, [loadAllPricing]);
  useEffect(() => { loadBookings(); }, [bookingView]);
  useEffect(() => { loadAffiliates(); }, [affiliateSort]);

  const sortData = (data, config) => [...data].sort((a, b) => { let aV = a[config.field], bV = b[config.field]; if (typeof aV === 'string') aV = aV.toLowerCase(); if (typeof bV === 'string') bV = bV.toLowerCase(); if (aV < bV) return config.direction === 'asc' ? -1 : 1; if (aV > bV) return config.direction === 'asc' ? 1 : -1; return 0; });
  const handleSort = (f) => setSortConfig(p => ({ field: f, direction: p.field === f && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleVehicleSort = (f) => setVehicleSortConfig(p => ({ field: f, direction: p.field === f && p.direction === 'asc' ? 'desc' : 'asc' }));

  const handleInlineBookingEdit = async (bookingId, field, value) => { const b = bookings.find(x => x.booking_id === bookingId); if (!b) return; const u = { ...b, [field]: value }; try { const r = await fetch(`${API}/api/pricing/bookings/${bookingId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date: u.date, service_type: u.service_type, vehicle_name: u.vehicle_name, hours: u.hours, pricing_type: u.pricing_type, ownership: u.ownership, retail_hourly: u.retail_hourly, affiliate_hourly: u.affiliate_hourly, rental_cost: u.rental_cost, driver_rate: u.driver_rate, notes: u.notes }) }); if (r.ok) { toast.success('Updated'); loadBookings(); } } catch { toast.error('Failed'); } };
  const handleInlineVehicleEdit = async (vehicleId, field, value) => { const v = vehicles.find(x => x.vehicle_id === vehicleId); if (!v) return; const u = { ...v, [field]: value }; try { const r = await fetch(`${API}/api/pricing/vehicles/${vehicleId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ vehicle_name: u.vehicle_name, category: u.category, ownership_default: u.ownership_default, retail_hourly: u.retail_hourly, affiliate_hourly: u.affiliate_hourly, rental_low: u.rental_low, rental_high: u.rental_high, cost_basis: u.cost_basis }) }); if (r.ok) { toast.success('Updated'); loadVehicles(); } } catch { toast.error('Failed'); } };

  const handleCreateBooking = async (e) => { e.preventDefault(); try { const r = await fetch(`${API}/api/pricing/bookings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newBooking) }); if (r.ok) { toast.success('Created'); setShowBookingForm(false); loadBookings(); } } catch { toast.error('Failed'); } };
  const handleCreateVehicle = async (e) => { e.preventDefault(); try { const r = await fetch(`${API}/api/pricing/vehicles`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newVehicle) }); if (r.ok) { toast.success('Created'); setShowVehicleForm(false); loadVehicles(); } } catch { toast.error('Failed'); } };
  const handleCreateAffiliate = async (e) => { e.preventDefault(); try { const r = await fetch(`${API}/api/pricing/affiliates`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newAffiliate) }); if (r.ok) { toast.success('Created'); setShowAffiliateForm(false); loadAffiliates(); } } catch { toast.error('Failed'); } };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${API}/api/pricing/bookings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...quickForm, revenue_total: quickForm.retail_hourly * quickForm.hours, client: quickForm.client_name })
      });
      if (r.ok) {
        toast.success('Booking created');
        setShowQuickAdd(false);
        setQuickForm({ client_name: '', client_phone: '', service_type: 'hourly', vehicle_name: '', date: new Date().toISOString().split('T')[0], pickup_time: '', hours: 3, pickup: '', dropoff: '', notes: '', retail_hourly: 0 });
        loadBookings(); loadAllPricing();
      }
    } catch { toast.error('Failed'); }
  };

  const deleteBooking = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/pricing/bookings/${id}`, { method: 'DELETE' }); loadBookings(); };
  const deleteVehicle = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/pricing/vehicles/${id}`, { method: 'DELETE' }); loadVehicles(); };
  const deleteAffiliate = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/pricing/affiliates/${id}`, { method: 'DELETE' }); loadAffiliates(); };

  const handleCalculate = async () => { try { const r = await fetch(`${API}/api/pricing/calculator`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...calcForm, date: new Date().toISOString().split('T')[0] }) }); setCalcResult(await r.json()); } catch { toast.error('Failed'); } };
  const calculateQuote = async () => { try { const r = await fetch(`${API}/api/pricing/quote/calculate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ vehicle_type: quoteVehicle, hours: quoteHours, pricing_type: quotePricing, driver_rate: quoteDriverRate, rental_cost: quoteRentalCost, is_rental: quoteIsRental }) }); setQuoteResult(await r.json()); } catch { toast.error('Failed'); } };

  const sortedBookings = sortData(bookings, sortConfig);
  const sortedVehicles = sortData(vehicles, vehicleSortConfig);

  const handleTabChange = (tab) => { setActiveTab(tab); setSearchParams({ tab }); };

  const quickActions = buildQuickActions({
    onQuickAdd: () => setShowQuickAdd(true),
    onQuickQuote: () => setShowCalculator(true),
    onLiveTrack: () => navigate('/admin/tracking'),
    onSnapshot: handleSnapshotSeed,
    snapshotRunning,
    GoogleCalendarButton: <GoogleCalendarButton />,
  });

  return (
    <AdminShell
      activeTab={activeTab}
      onTabChange={handleTabChange}
      title="ACE Command Center"
      subtitle="Dispatch · Fleet · CRM"
      quickActions={quickActions}
    >
      <div data-testid="command-center"
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <Tabs value={activeTab} onValueChange={handleTabChange} data-testid="main-tabs">
          {/* Tab triggers are now handled by AdminShell SubTabStrip; keep an empty TabsList for accessibility */}
          <TabsList className="hidden">
            {[
              'ops', 'dailyops', 'quotebookings', 'teampay',
              'weeklypl', 'monthlypl', 'contracts', 'quo', 'clients', 'fulfillment',
            ].map(v => <TabsTrigger key={v} value={v}>{v}</TabsTrigger>)}
          </TabsList>

          {/* Operations Dashboard — new landing view */}
          <TabsContent value="ops"><OpsDashboardTab onJumpTab={handleTabChange} /></TabsContent>

          {/* Daily Ops Log */}
          <TabsContent value="opslogs">
            <OpsLogsTab />
          </TabsContent>

          <TabsContent value="dailyops">
            <CommandDashboardTab />
            <div className="mt-6"><DailyOpsTab /></div>
            <div className="mt-6"><OpsLogV3Tab /></div>
          </TabsContent>

          {/* Calendar-first Quotes & Bookings — Get Your Quote widget above Vehicle Tiers */}
          <TabsContent value="quotebookings">
            <RevenueCalendarTab onQuickQuote={handleCalendarQuickQuote} />
            <div className="mt-6" id="quick-quote-anchor"><QuickQuoteTab prefillDate={qqPrefillDate} /></div>
            <div className="mt-6" id="requests-inbox-anchor"><RequestsInboxTab /></div>
            <div className="mt-6"><BookingsVIPTab /></div>
            <div className="mt-6" id="rental-requests-anchor"><RentalRequestsTab /></div>
          </TabsContent>

          {/* Drivers + Affiliates + Commissions + Owner Payouts */}
          <TabsContent value="teampay">
            <DriverRosterTab />
            <div className="mt-6"><DriverPayrollTab /></div>
            <div className="mt-6"><AffiliateNetworkTab /></div>
            <div className="mt-6"><PartnerNetworkTab /></div>
            <div className="mt-6"><CommissionsTab /></div>
            <div className="mt-6" id="owner-payouts-anchor"><OwnerPayoutsTab /></div>
          </TabsContent>

          {/* Weekly Review + Vehicle P&L + Maintenance + Condition Reports */}
          <TabsContent value="weeklypl">
            <WeeklyReviewTab />
            <div className="mt-6"><VehiclePLTab /></div>
            <div className="mt-6" id="maintenance-anchor"><MaintenanceTab /></div>
            <div className="mt-6" id="condition-reports-anchor"><ConditionReportsTab /></div>
          </TabsContent>

          {/* Monthly P&L */}
          <TabsContent value="monthlypl"><MonthlyPLTab /></TabsContent>

          <TabsContent value="contracts">
            <QuoteContractBuilder />
            <div className="mt-6"><InvoicesGeneratorTab /></div>
          </TabsContent>
          <TabsContent value="quo"><QuoTab /></TabsContent>

          {/* Unified Clients hub (replaces CRM + Contacts + Consulting + VIP) */}
          <TabsContent value="clients">
            <ClientCRM />
            <div className="mt-6"><FollowUpTab /></div>
          </TabsContent>

          {/* Driven Collective Fulfillment Control (P7.1) */}
          <TabsContent value="fulfillment">
            <FulfillmentControlTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <BookingModal show={showBookingForm} onClose={() => setShowBookingForm(false)} newBooking={newBooking} setNewBooking={setNewBooking} onSubmit={handleCreateBooking} />
      <VehicleModal show={showVehicleForm} onClose={() => setShowVehicleForm(false)} newVehicle={newVehicle} setNewVehicle={setNewVehicle} onSubmit={handleCreateVehicle} />
      <AffiliateModal show={showAffiliateForm} onClose={() => setShowAffiliateForm(false)} newAffiliate={newAffiliate} setNewAffiliate={setNewAffiliate} onSubmit={handleCreateAffiliate} />
      <CalculatorModal show={showCalculator} onClose={() => setShowCalculator(false)} calcForm={calcForm} setCalcForm={setCalcForm} calcResult={calcResult} onCalculate={handleCalculate} />

      {/* Quick Add Booking Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowQuickAdd(false)}>
          <Card className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="quick-add-modal">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between rounded-t-2xl z-10">
              <h3 className="font-bold text-sm">Quick Add Booking</h3>
              <button onClick={() => setShowQuickAdd(false)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleQuickAdd} className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] text-gray-500 block mb-1">Client Name *</label><Input value={quickForm.client_name} onChange={e => setQuickForm(p => ({...p, client_name: e.target.value}))} required className="h-11 sm:h-9 text-sm border-gray-200" data-testid="qa-client-name" /></div>
                <div><label className="text-[10px] text-gray-500 block mb-1">Phone</label><Input type="tel" value={quickForm.client_phone} onChange={e => setQuickForm(p => ({...p, client_phone: e.target.value}))} className="h-11 sm:h-9 text-sm border-gray-200" data-testid="qa-phone" /></div>
              </div>
              <div><label className="text-[10px] text-gray-500 block mb-1">Service</label>
                <Select value={quickForm.service_type} onValueChange={v => setQuickForm(p => ({...p, service_type: v}))}><SelectTrigger className="h-11 sm:h-9 text-sm border-gray-200"><SelectValue /></SelectTrigger><SelectContent>{['airport-transfer','point-to-point','hourly','event-wedding','corporate','nightlife'].map(s => <SelectItem key={s} value={s}>{s.replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-[10px] text-gray-500 block mb-1">Vehicle</label>
                <Select value={quickForm.vehicle_name} onValueChange={v => { const veh = vehicles.find(x => x.vehicle_name === v); setQuickForm(p => ({...p, vehicle_name: v, retail_hourly: veh?.retail_hourly || p.retail_hourly})); }}>
                  <SelectTrigger className="h-11 sm:h-9 text-sm border-gray-200"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{vehicles.map(v => <SelectItem key={v.vehicle_id} value={v.vehicle_name}>{v.vehicle_name} — ${v.retail_hourly}/hr</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-[10px] text-gray-500 block mb-1">Date *</label><Input type="date" value={quickForm.date} onChange={e => setQuickForm(p => ({...p, date: e.target.value}))} required className="h-11 sm:h-9 text-sm border-gray-200" /></div>
                <div><label className="text-[10px] text-gray-500 block mb-1">Time *</label><Input type="time" value={quickForm.pickup_time} onChange={e => setQuickForm(p => ({...p, pickup_time: e.target.value}))} required className="h-11 sm:h-9 text-sm border-gray-200" /></div>
                <div><label className="text-[10px] text-gray-500 block mb-1">Hours</label><Input type="number" min={3} value={quickForm.hours} onChange={e => setQuickForm(p => ({...p, hours: parseInt(e.target.value)||3}))} className="h-11 sm:h-9 text-sm border-gray-200" /></div>
              </div>
              <div><label className="text-[10px] text-gray-500 block mb-1">Pickup Location</label><Input value={quickForm.pickup} onChange={e => setQuickForm(p => ({...p, pickup: e.target.value}))} placeholder="Address or landmark" className="h-11 sm:h-9 text-sm border-gray-200" /></div>
              <div><label className="text-[10px] text-gray-500 block mb-1">Drop-off</label><Input value={quickForm.dropoff} onChange={e => setQuickForm(p => ({...p, dropoff: e.target.value}))} placeholder="Address or landmark" className="h-11 sm:h-9 text-sm border-gray-200" /></div>
              <div><label className="text-[10px] text-gray-500 block mb-1">Notes</label><Input value={quickForm.notes} onChange={e => setQuickForm(p => ({...p, notes: e.target.value}))} placeholder="Special requests..." className="h-11 sm:h-9 text-sm border-gray-200" /></div>
              {quickForm.retail_hourly > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Estimated Total</p>
                  <p className="text-2xl font-bold text-red-600">${quickForm.retail_hourly * quickForm.hours}</p>
                  <p className="text-[10px] text-gray-400">{quickForm.hours}hrs @ ${quickForm.retail_hourly}/hr</p>
                </div>
              )}
              <Button type="submit" className="w-full h-12 sm:h-10 bg-red-600 hover:bg-red-700 text-sm font-semibold" data-testid="qa-submit">Create Booking</Button>
            </form>
          </Card>
        </div>
      )}
    </AdminShell>
  );
};

export default CommandCenter;
